'use client';

import { useProductImageAsset } from '@/domain/products/hooks/useProductImageAsset';
import { type Product, type ProductInsertImageAssetDTO } from '@/domain/products/types';
import { resolveAssetUrl } from '@/domain/products/utils/resolveImageUrl';
import { ImageViewer } from '@andritzot/metris-web-ui/data-display/image';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';

import { OverviewImageCarousel } from './OverviewImageCarousel';

interface OverviewProductImagesProps {
  product: Product;
}

interface OverviewMainImageProps extends OverviewProductImagesProps {
  bleed?: number;
}

const MAIN_IMAGE_MAX_HEIGHT = '400px';

const bySortOrder = (a: ProductInsertImageAssetDTO, b: ProductInsertImageAssetDTO) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

export const OverviewMainImage = ({ product, bleed = 0 }: OverviewMainImageProps) => {
  const { data: assets = [] } = useProductImageAsset(product.idProduct);

  const mainAsset = product.headerImageId ? assets.find((asset) => asset.id === product.headerImageId) : undefined;
  const src = resolveAssetUrl(mainAsset?.blobName);

  if (!src) return null;

  return (
    <Box sx={{ mx: -bleed, mt: -bleed, mb: 4 }}>
      <ImageViewer
        src={src}
        alt={mainAsset?.altText || product.product}
        width="100%"
        loading="eager"
        preview
        style={{ width: '100%', display: 'block' }}
        imageStyle={{
          width: '100%',
          height: 'auto',
          maxHeight: MAIN_IMAGE_MAX_HEIGHT,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
};

export const OverviewImageGallery = ({ product }: OverviewProductImagesProps) => {
  const { data: assets = [], isLoading } = useProductImageAsset(product.idProduct);

  const images = assets
    .filter((asset) => asset.id !== product.headerImageId)
    .sort(bySortOrder)
    .map((asset) => ({ src: resolveAssetUrl(asset.blobName), caption: asset.altText || undefined }))
    .filter((image) => image.src);

  if (!isLoading && images.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <OverviewImageCarousel images={images} loading={isLoading} height="150px" />
    </Box>
  );
};
